import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { TutorialDefinition, getTutorialById } from '@/components/tutorials/tutorialDefinitions';

interface TutorialContextType {
  currentTutorial: TutorialDefinition | null;
  isOpen: boolean;
  completedTutorials: string[];
  isLoaded: boolean;
  isDrawerOpen: boolean;
  showTutorial: (id: string) => void;
  closeTutorial: () => void;
  markComplete: (id: string) => Promise<void>;
  hasCompleted: (id: string) => boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const TutorialProvider = ({ children }: { children: ReactNode }) => {
  const { user, profile } = useAuth();
  const [currentTutorial, setCurrentTutorial] = useState<TutorialDefinition | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [completedTutorials, setCompletedTutorials] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Load completed tutorials from profile
  useEffect(() => {
    if (!profile) return;
    const tutorialsCompleted = profile.tutorials_completed;
    if (Array.isArray(tutorialsCompleted)) {
      setCompletedTutorials(tutorialsCompleted as string[]);
    } else {
      setCompletedTutorials([]);
    }
    setIsLoaded(true);
  }, [profile?.tutorials_completed]);

  const showTutorial = useCallback((id: string) => {
    const tutorial = getTutorialById(id);
    if (tutorial) {
      setCurrentTutorial(tutorial);
      setIsOpen(true);
      setIsDrawerOpen(false);
    }
  }, []);

  const closeTutorial = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setCurrentTutorial(null), 300);
  }, []);

  const markComplete = useCallback(async (id: string) => {
    if (!user?.id || completedTutorials.includes(id)) return;

    const newCompleted = [...completedTutorials, id];
    setCompletedTutorials(newCompleted);

    try {
      await supabase
        .from('profiles')
        .update({ tutorials_completed: newCompleted })
        .eq('id', user.id);
    } catch (error) {
      console.error('Error saving tutorial completion:', error);
    }
  }, [user?.id, completedTutorials]);

  const hasCompleted = useCallback((id: string) => {
    return completedTutorials.includes(id);
  }, [completedTutorials]);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        currentTutorial,
        isOpen,
        completedTutorials,
        isLoaded,
        isDrawerOpen,
        showTutorial,
        closeTutorial,
        markComplete,
        hasCompleted,
        openDrawer,
        closeDrawer,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
